import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Input, Spinner, Alert, Row, Col, Label, FormGroup, CustomInput, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { IApplicationState } from '../../redux/reducers';
import { IProduct } from '../../interfaces/products';
import productService from '../../services/productService';
import { generateProductAPI } from '../../services/productGenerationAPI';
import { detectIntent } from '../../utils/intentDetector';
import { Box, BarChart2, FileText, Zap, Mic, Send, Star, Menu, Plus, MessageCircle, Check } from 'react-feather';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';

// New chat imports
import {
  createChatSession,
  sendChatMessage,
  loadChatSession,
  loadUserChatSessions,
  setCurrentSession,
  clearChatMessages,
  addOptimisticMessage,
  updateMessageStatus,
  removeOptimisticMessage,
  deleteChatSession
} from '../../redux/actions/chat/chatActions';
import { ChatMessage as ChatMessageType } from '../../services/chatService';
import ChatMessage from '../../components/chat/ChatMessage';
import SessionSidebar from '../../components/chat/SessionSidebar';
import ChatLoading from '../../components/chat/ChatLoading';

// Modal imports
import GenerateProductModal from '../../components/modals/GenerateProductModal';
import FormulationModal from '../../components/modals/FormulationModal';
import ScoreCalculationModal from '../../components/modals/ScoreCalculationModal';
import OptimizationModal from '../../components/modals/OptimizationModal';
import GeneratedProductCard from './components/GeneratedProductCard';

// Styles
import '../../assets/scss/pages/aichat/_aiChatDashboard.scss';
import '../../assets/scss/components/chat/_chat-message.scss';
import '../../assets/scss/components/chat/_session-sidebar.scss';
import '../../assets/scss/components/chat/_chat-interface.scss';
import '../../assets/scss/components/chat/_chat-loading.scss';
import '../../assets/scss/components/chat/_chat-ipad.scss';

interface QuickActionCard {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

interface AIChatDashboardProps {
  className?: string;
}

const COMMON_ALLERGENS = [
  "Milk", "Eggs", "Fish", "Shellfish", "Tree Nuts", "Peanuts", "Wheat", "Soybeans"
];

const DIETARY_OPTIONS = [
  "Vegan", "Vegetarian", "Gluten-Free", "Keto", "Paleo", "Organic", "Non-GMO", "Halal", "Kosher"
];

const AIChatDashboard: React.FC<AIChatDashboardProps> = ({ className = '' }) => {
  const dispatch = useDispatch();
  const { user, chat } = useSelector((state: IApplicationState) => state);
  const { profile, selected_brand } = user;
  const company = profile?.company;

  // Chat state from Redux
  const {
    currentSessionId,
    currentSessionTitle,
    messages: chatMessages,
    userSessions,
    isCreatingSession,
    isSendingMessage,
    isLoadingSession,
    isLoadingUserSessions,
    isAITyping,
    createSessionError,
    sendMessageError,
    loadSessionError
  } = chat;

  // Local UI state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showFormulationModal, setShowFormulationModal] = useState(false);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [showOptimizationModal, setShowOptimizationModal] = useState(false);
  const [modalPrefillData, setModalPrefillData] = useState<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Track current session ID with a ref to avoid stale closures in async handlers
  const currentSessionIdRef = useRef<string | null>(null);
  useEffect(() => {
    currentSessionIdRef.current = currentSessionId;
  }, [currentSessionId]);

  // Track pending session creation to prevent multiple simultaneous sessions
  const [isCreatingSessionLocal, setIsCreatingSessionLocal] = useState(false);
  const pendingSessionPromise = useRef<any>(null);

  // Action selection state
  const [selectedActionType, setSelectedActionType] = useState<"new_product" | "formulate" | "score" | "optimize" | null>(null);

  // Session creation modal state
  const [showSessionTitleModal, setShowSessionTitleModal] = useState(false);
  const [sessionTitle, setSessionTitle] = useState('');
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Device detection for responsive behavior
  const { isIPad, isTablet, screenWidth, orientation, isIPadPro } = useDeviceDetection();

  // Initialize sidebar state based on device type
  useEffect(() => {
    // Hide sidebar by default on iPad and tablets
    if (isIPad || isTablet) {
      setSidebarOpen(false);
    } else {
      // Keep sidebar open by default on desktop
      setSidebarOpen(true);
    }
  }, [isIPad, isTablet]);

  // Shared UI state
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  // New Product State
  const [newProductName, setNewProductName] = useState('');
  const [isGeneratingProduct, setIsGeneratingProduct] = useState(false);

  // Score Calculation State
  const [allProducts, setAllProducts] = useState<IProduct[]>([]);
  const [selectedScoreProductId, setSelectedScoreProductId] = useState<string>("");
  const [isCalculatingScore, setIsCalculatingScore] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  // Formulation State
  const [formulateMode, setFormulateMode] = useState<'quick' | 'detailed'>('quick');
  const [isGeneratingFormulation, setIsGeneratingFormulation] = useState(false);
  const [formulateType, setFormulateType] = useState('');
  const [formulateServingSize, setFormulateServingSize] = useState<number>(0);
  const [formulateMaxCost, setFormulateMaxCost] = useState<string>("");
  const [formulateNutrition, setFormulateNutrition] = useState({
    calories: '', protein: '', carbs: '', fat: '', fiber: ''
  });
  const [formulateAllergens, setFormulateAllergens] = useState<string[]>([]);
  const [formulateDietary, setFormulateDietary] = useState<string[]>([]);

  // Optimization State
  const [allFormulations, setAllFormulations] = useState<any[]>([]);
  const [selectedOptimizationRecipeId, setSelectedOptimizationRecipeId] = useState<string>("");
  const [selectedOptimizationObjective, setSelectedOptimizationObjective] = useState<string>("lower_sugar");
  const [isOptimizingRecipe, setIsOptimizingRecipe] = useState(false);
  const [optimizeMaxCostIncrease, setOptimizeMaxCostIncrease] = useState<number>(10);
  const [optimizeMaintainTaste, setOptimizeMaintainTaste] = useState<boolean>(true);
  const [optimizeAllergens, setOptimizeAllergens] = useState<string[]>([]);
  const [optimizeDietary, setOptimizeDietary] = useState<string[]>([]);
  const [isLoadingFormulations, setIsLoadingFormulations] = useState(false);

  // Fetch products when score action is selected
  useEffect(() => {
    if (selectedActionType === 'score') {
      const fetchProducts = async () => {
        setIsLoadingProducts(true);
        try {
          const activeProducts = await (dispatch as any)(productService.getAllProductsForFilters());
          const conceptProducts = await (dispatch as any)(productService.getAllConceptProductsForFilters());

          let products: IProduct[] = [];
          if (Array.isArray(activeProducts)) products = [...products, ...activeProducts];
          if (Array.isArray(conceptProducts)) products = [...products, ...conceptProducts];

          // Remove duplicates and sort by name
          const uniqueProducts = Array.from(new Map(products.map(item => [item.id, item])).values());
          uniqueProducts.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

          setAllProducts(uniqueProducts);
        } catch (error) {
          console.error("Failed to fetch products for score calculation:", error);
        } finally {
          setIsLoadingProducts(false);
        }
      };

      fetchProducts();
    }
  }, [selectedActionType, dispatch]);

  // Fetch formulations when optimize action is selected
  useEffect(() => {
    if (selectedActionType === 'optimize') {
      const fetchFormulations = async () => {
        setIsLoadingFormulations(true);
        try {
          const { getAllFormulationsAPI } = await import('../../services/formulationService');
          const result = await getAllFormulationsAPI();
          if (result.success && result.data) {
            setAllFormulations(result.data);
          }
        } catch (error) {
          console.error("Failed to fetch formulations for optimization:", error);
        } finally {
          setIsLoadingFormulations(false);
        }
      };

      fetchFormulations();
    }
  }, [selectedActionType]);

  const handleChatNewProduct = async () => {
    if (!inputMessage.trim()) return;

    setIsGeneratingProduct(true);
    const description = inputMessage.trim();
    const productName = newProductName.trim();

    // Send optimistic message
    const optimisticMessage = {
      message_id: `temp_product_${Date.now()}`,
      timestamp: new Date().toISOString(),
      role: 'user' as const,
      content: `Generate new product: ${productName || description}`,
      action_type: 'new_product' as const,
      status: 'sending' as const,
      isOptimistic: true
    };

    setInputMessage('');
    setNewProductName('');
    dispatch(addOptimisticMessage(optimisticMessage));

    try {
      const result = await generateProductAPI({ description });

      const messageText = result.success
        ? `New product generated: ${result.product?.name || productName || description}`
        : `Product generation failed: ${result.error || 'AI service temporarily unavailable'}`;

      const metadata = result.success
        ? { type: 'product_generated', data: result.product }
        : { type: 'product_failed', error: result.error };

      const completionResult = await handleActionCompletion(
        messageText,
        metadata,
        'new_product'
      );

      if (completionResult.success) {
        dispatch(removeOptimisticMessage(optimisticMessage.message_id));
      } else {
        dispatch(updateMessageStatus(optimisticMessage.message_id, 'failed'));
      }

      if (!result.success) {
        setInputMessage(description);
      }
    } catch (error) {
      dispatch(updateMessageStatus(optimisticMessage.message_id, 'failed'));
      console.error("Error in chat product generation:", error);
      setInputMessage(description);

      // Still try to persist the error to chat
      await handleActionCompletion(
        `Error generating product: ${error instanceof Error ? error.message : String(error)}`,
        { type: 'product_failed', error: String(error) },
        'new_product'
      );
    } finally {
      setIsGeneratingProduct(false);
    }
  };

  const handleChatScoreCalculation = async () => {
    if (!selectedScoreProductId) return;

    setIsCalculatingScore(true);

    const product = allProducts.find(p => p.id === selectedScoreProductId);
    const productName = product ? product.name : 'Product';

    const optimisticMessage = {
      message_id: `temp_score_${Date.now()}`,
      timestamp: new Date().toISOString(),
      role: 'user' as const,
      content: `Calculate nutritional scores for ${productName}`,
      action_type: 'score' as const,
      status: 'sending' as const,
      isOptimistic: true
    };
    dispatch(addOptimisticMessage(optimisticMessage));

    try {
      const { calculateScoreAPI } = await import('../../services/formulationService');
      const result = await calculateScoreAPI(selectedScoreProductId);

      const messageText = result.success
        ? `Performance scores calculated for ${productName}`
        : `Score calculation failed for ${productName}: ${result.error || 'Server error'}`;

      const metadata = result.success
        ? { type: 'score_calculated', data: result.data }
        : { type: 'score_failed', error: result.error };

      const completionResult = await handleActionCompletion(
        messageText,
        metadata,
        'score'
      );

      if (completionResult.success) {
        dispatch(removeOptimisticMessage(optimisticMessage.message_id));
        if (result.success) setSelectedScoreProductId("");
      } else {
        dispatch(updateMessageStatus(optimisticMessage.message_id, 'failed'));
      }
    } catch (error) {
      dispatch(updateMessageStatus(optimisticMessage.message_id, 'failed'));
      console.error("Error in chat score calculation:", error);

      await handleActionCompletion(
        `Error calculating scores: ${error instanceof Error ? error.message : String(error)}`,
        { type: 'score_failed', error: String(error) },
        'score'
      );
    } finally {
      setIsCalculatingScore(false);
    }
  };

  const handleChatFormulation = async () => {
    if (!inputMessage.trim()) return;

    setIsGeneratingFormulation(true);
    const description = inputMessage.trim();
    const maxCostStr = formulateMaxCost ? formulateMaxCost : undefined;
    const maxCost = maxCostStr ? parseFloat(maxCostStr) : undefined;

    const optimisticMessage = {
      message_id: `temp_formulate_${Date.now()}`,
      timestamp: new Date().toISOString(),
      role: 'user' as const,
      content: `Generate formulation: ${description}${maxCost ? ` (Max Cost: $${maxCost})` : ''}`,
      action_type: 'formulate' as const,
      status: 'sending' as const,
      isOptimistic: true
    };

    setInputMessage('');
    setFormulateMaxCost('');
    dispatch(addOptimisticMessage(optimisticMessage));

    try {
      const { generateFormulationAPI, createFormulationAPI } = await import('../../services/formulationService');

      let result;
      if (formulateMode === 'quick') {
        result = await generateFormulationAPI({
          description,
          max_cost: maxCost
        });
      } else {
        result = await createFormulationAPI({
          description,
          product_type: formulateType,
          serving_size: formulateServingSize,
          target_nutrition: {
            calories: formulateNutrition.calories ? parseFloat(formulateNutrition.calories) : undefined,
            protein: formulateNutrition.protein ? parseFloat(formulateNutrition.protein) : undefined,
            carbs: formulateNutrition.carbs ? parseFloat(formulateNutrition.carbs) : undefined,
            fat: formulateNutrition.fat ? parseFloat(formulateNutrition.fat) : undefined,
            fiber: formulateNutrition.fiber ? parseFloat(formulateNutrition.fiber) : undefined
          },
          constraints: {
            max_cost: maxCost,
            allergens: formulateAllergens.length > 0 ? formulateAllergens : undefined,
            dietary: formulateDietary.length > 0 ? formulateDietary : undefined
          }
        });
      }

      const messageText = result.success
        ? `New formulation generated for: ${description}`
        : `Formulation failed: ${result.error || 'Server error'}`;

      const metadata = result.success
        ? { type: 'formulation_created', data: result.data }
        : { type: 'formulation_failed', error: result.error };

      const completionResult = await handleActionCompletion(
        messageText,
        metadata,
        'formulate'
      );

      if (completionResult.success) {
        dispatch(removeOptimisticMessage(optimisticMessage.message_id));
      } else {
        dispatch(updateMessageStatus(optimisticMessage.message_id, 'failed'));
      }

      if (!result.success) {
        setInputMessage(description);
      }
    } catch (error) {
      dispatch(updateMessageStatus(optimisticMessage.message_id, 'failed'));
      console.error("Error in chat formulation:", error);
      setInputMessage(description);

      await handleActionCompletion(
        `Error generating formulation: ${error instanceof Error ? error.message : String(error)}`,
        { type: 'formulation_failed', error: String(error) },
        'formulate'
      );
    } finally {
      setIsGeneratingFormulation(false);
    }
  };

  const handleChatOptimization = async () => {
    if (!selectedOptimizationRecipeId) return;

    setIsOptimizingRecipe(true);

    const recipe = allFormulations.find(f => f.id === selectedOptimizationRecipeId);
    const recipeName = recipe ? recipe.name : 'Recipe';
    const objectiveLabel = selectedOptimizationObjective.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

    const optimisticMessage = {
      message_id: `temp_optimize_${Date.now()}`,
      timestamp: new Date().toISOString(),
      role: 'user' as const,
      content: `Optimize ${recipeName} to ${objectiveLabel}`,
      action_type: 'optimize' as const,
      status: 'sending' as const,
      isOptimistic: true
    };
    dispatch(addOptimisticMessage(optimisticMessage));

    try {
      const { optimizeRecipeAPI } = await import('../../services/formulationService');
      const result = await optimizeRecipeAPI(selectedOptimizationRecipeId, {
        objective: selectedOptimizationObjective as any,
        max_cost_increase: optimizeMaxCostIncrease,
        maintain_taste: optimizeMaintainTaste,
        allergen_restrictions: optimizeAllergens.length > 0 ? optimizeAllergens : undefined,
        dietary_requirements: optimizeDietary.length > 0 ? optimizeDietary : undefined
      });

      const messageText = result.success
        ? `Optimized recipe created for ${recipeName} (Objective: ${objectiveLabel})`
        : `Optimization failed for ${recipeName}: ${result.error || 'Server error'}`;

      const metadata = result.success
        ? { type: 'recipe_optimized', data: result.data }
        : { type: 'optimization_failed', error: result.error };

      const completionResult = await handleActionCompletion(
        messageText,
        metadata,
        'optimize'
      );

      if (completionResult.success) {
        dispatch(removeOptimisticMessage(optimisticMessage.message_id));
        if (result.success) setSelectedOptimizationRecipeId("");
      } else {
        dispatch(updateMessageStatus(optimisticMessage.message_id, 'failed'));
      }
    } catch (error) {
      dispatch(updateMessageStatus(optimisticMessage.message_id, 'failed'));
      console.error("Error in chat optimization:", error);

      await handleActionCompletion(
        `Error optimizing recipe: ${error instanceof Error ? error.message : String(error)}`,
        { type: 'optimization_failed', error: String(error) },
        'optimize'
      );
    } finally {
      setIsOptimizingRecipe(false);
    }
  };

  const actions: QuickActionCard[] = [
    {
      id: 'new-product',
      title: 'New Product',
      description: 'Create a new product concept.',
      icon: <Box size={24} />,
      color: '#F5F7FA'
    },
    {
      id: 'new-scores',
      title: 'New Score',
      description: 'Calculate performance scores.',
      icon: <BarChart2 size={24} />,
      color: '#F5F7FA'
    },
    {
      id: 'formulate',
      title: 'Formulate',
      description: 'Create ingredient formulations.',
      icon: <FileText size={24} />,
      color: '#F5F7FA'
    },
    {
      id: 'optimize-recipe',
      title: 'Optimize Recipe',
      description: 'Improve an existing recipe.',
      icon: <Zap size={24} />,
      color: '#F5F7FA'
    }
  ];

  const quickSuggestions = [
    'Create a new energy drink',
    'Calculate supplier scores',
    'Formulate a protein bar'
  ];

  // Get contextual suggestions based on selected action
  const getContextualSuggestions = () => {
    if (!selectedActionType) {
      return quickSuggestions;
    }

    const contextualSuggestions = {
      'new_product': [
        'Create a healthy energy drink',
        'Design a plant-based protein bar',
        'Develop a low-sugar snack'
      ],
      'score': [
        'Calculate nutritional scores',
        'Analyze sustainability metrics',
        'Compare supplier ratings'
      ],
      'formulate': [
        'Create a protein powder blend',
        'Formulate a vitamin supplement',
        'Design a functional beverage'
      ],
      'optimize': [
        'Reduce sugar content',
        'Improve protein levels',
        'Lower production costs'
      ]
    };

    return contextualSuggestions[selectedActionType] || quickSuggestions;
  };

  // Load user sessions on component mount
  useEffect(() => {
    if (profile?.id) {
      dispatch(loadUserChatSessions(profile.id));
    }
  }, [dispatch, profile?.id]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [chatMessages, isAITyping]);

  // Reset pending session promise when session is created
  useEffect(() => {
    if (currentSessionId && pendingSessionPromise.current) {
      pendingSessionPromise.current = null;
      setIsCreatingSessionLocal(false);
    }
  }, [currentSessionId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      pendingSessionPromise.current = null;
      setIsCreatingSessionLocal(false);
    };
  }, []);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Reset action selection when starting new chat
  const handleNewChat = async () => {
    // Auto-generate session title based on selected action
    const defaultTitle = selectedActionType
      ? `${actions.find(a => getActionType(a.id) === selectedActionType)?.title} Session`
      : 'New AI Session';

    setSessionTitle(defaultTitle);
    setShowSessionTitleModal(true);
  };

  // Handle session title submission
  const handleCreateSession = async () => {
    if (!sessionTitle.trim()) {
      return;
    }

    // Clear current state
    dispatch(clearChatMessages());
    dispatch(setCurrentSession(null));

    // Create session with custom title
    try {
      setIsCreatingSessionLocal(true);
      const result: any = await (dispatch as any)(createChatSession({
        first_message: sessionTitle.trim(),
        action_type: selectedActionType || 'new_product'
      }));

      if (result.success) {
        // Reset modal state
        setShowSessionTitleModal(false);
        setSessionTitle('');
        setIsCreatingSessionLocal(false);
        setSidebarOpen(false);
      } else {
        setIsCreatingSessionLocal(false);
        console.error('Failed to create session:', result.error);
      }
    } catch (error) {
      setIsCreatingSessionLocal(false);
      console.error('Failed to create session:', error);
    }
  };

  // Cancel session creation
  const handleCancelSessionCreation = () => {
    setShowSessionTitleModal(false);
    setSessionTitle('');
  };

  // Load existing chat session
  const handleSessionSelect = async (sessionId: string) => {
    if (sessionId !== currentSessionId) {
      // Clear current messages immediately to show loading state
      dispatch(clearChatMessages());
      dispatch(setCurrentSession(sessionId));

      // Keep action cards visible but reset selection for existing sessions
      // User can still change action type even in existing sessions

      // Load the session messages
      dispatch(loadChatSession(sessionId));
    }
    setSidebarOpen(false);
  };

  // Map action type to chat API action_type
  const getActionType = (actionId: string): "new_product" | "formulate" | "score" | "optimize" => {
    switch (actionId) {
      case 'new-product':
        return 'new_product';
      case 'new-scores':
        return 'score';
      case 'formulate':
        return 'formulate';
      case 'optimize-recipe':
        return 'optimize';
      default:
        return 'new_product';
    }
  };

  // Handle action button clicks - and move to inline forms
  const handleActionClick = async (actionId: string, prefillData?: any) => {
    const actionType = getActionType(actionId);

    // Set the selected action type
    setSelectedActionType(actionType);
    setShowAdvancedOptions(false); // Reset advanced options when switching

    // Store prefill data if provided
    if (prefillData) {
      setModalPrefillData(prefillData);
    } else {
      setModalPrefillData(null);
    }

    // Auto-create session if one doesn't exist
    if (!currentSessionId && !isCreatingSessionLocal && !isCreatingSession) {
      try {
        setIsCreatingSessionLocal(true);
        const title = `${actions.find(a => getActionType(a.id) === actionType)?.title} Session`;

        const createPromise = (dispatch as any)(createChatSession({
          first_message: title,
          action_type: actionType
        }));

        pendingSessionPromise.current = createPromise;
        const createResult = await createPromise;

        if (createResult.success && createResult.data?.session_id) {
          // Refresh session history
          if (profile?.id) dispatch(loadUserChatSessions(profile.id));
        }
      } catch (error) {
        console.error('Failed to auto-create session on action select:', error);
      } finally {
        setIsCreatingSessionLocal(false);
        pendingSessionPromise.current = null;
      }
    }

    // Close sidebar on mobile
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  // Handle text message sending - now redirects to specialized handlers or sends standard messages
  const handleSendMessage = async () => {
    if (!inputMessage.trim() &&
      selectedActionType !== 'optimize' &&
      selectedActionType !== 'score' &&
      selectedActionType !== 'new_product') return;

    const userMessage = inputMessage.trim();

    // Detect intent from user message if no action is explicitly selected
    let targetActionType: "new_product" | "formulate" | "score" | "optimize" | null = selectedActionType;
    if (!selectedActionType && userMessage) {
      const detectedIntent = detectIntent(userMessage);
      if (detectedIntent.type === 'formulate') targetActionType = 'formulate';
      else if (detectedIntent.type === 'score') targetActionType = 'score';
      else if (detectedIntent.type === 'optimize') targetActionType = 'optimize';
      else if (detectedIntent.type === 'product') targetActionType = 'new_product';
    }

    // Redirect to specialized handlers based on action type
    if (targetActionType === 'formulate') {
      if (!selectedActionType) setSelectedActionType('formulate');
      await handleChatFormulation();
      return;
    }

    if (targetActionType === 'score') {
      if (!selectedActionType) setSelectedActionType('score');
      await handleChatScoreCalculation();
      return;
    }

    if (targetActionType === 'new_product') {
      if (!selectedActionType) setSelectedActionType('new_product');
      await handleChatNewProduct();
      return;
    }

    if (targetActionType === 'optimize') {
      if (!selectedActionType) setSelectedActionType('optimize');
      await handleChatOptimization();
      return;
    }

    if (!userMessage) return;

    // Use selected action type or default to new_product
    const actionType: "new_product" | "formulate" | "score" | "optimize" = selectedActionType || 'new_product';

    // If no current session, create one automatically
    let activeSessionId = currentSessionIdRef.current;

    // If a session is being created, wait for it and get the ID
    if (!activeSessionId && isCreatingSessionLocal && pendingSessionPromise.current) {
      try {
        const createResult = await pendingSessionPromise.current;
        if (createResult && createResult.success && createResult.data?.session_id) {
          activeSessionId = createResult.data.session_id;
        }
      } catch (error) {
        console.error('Pending session creation failed in sendMessage:', error);
      }
    }

    if (!activeSessionId) {
      try {
        setIsCreatingSessionLocal(true);
        const createPromise = (dispatch as any)(createChatSession({
          first_message: userMessage,
          action_type: actionType
        }));

        pendingSessionPromise.current = createPromise;
        const createResult: any = await createPromise;

        if (createResult.success && createResult.data?.session_id) {
          activeSessionId = createResult.data.session_id;
          // Refresh session history
          if (profile?.id) dispatch(loadUserChatSessions(profile.id));
        } else {
          return;
        }
      } catch (error) {
        console.error('Failed to auto-create session on message send:', error);
        return;
      } finally {
        setIsCreatingSessionLocal(false);
        pendingSessionPromise.current = null;
      }
    }

    // Create optimistic message
    const optimisticMessage = {
      message_id: `temp_${Date.now()}`,
      timestamp: new Date().toISOString(),
      role: 'user' as const,
      content: userMessage,
      action_type: actionType,
      status: 'sending' as const,
      isOptimistic: true
    };

    // Clear input and add optimistic message immediately
    setInputMessage('');
    dispatch(addOptimisticMessage(optimisticMessage));

    // Send message to session
    const metadata: any = {};
    if (!selectedActionType) {
      const detectedIntent = detectIntent(userMessage);
      if (detectedIntent.extractedData) {
        Object.assign(metadata, detectedIntent.extractedData);
      }
    }

    if (modalPrefillData) {
      if (modalPrefillData.serving_size) metadata.serving_size = modalPrefillData.serving_size;
      if (modalPrefillData.target_nutrition) metadata.target_nutrition = modalPrefillData.target_nutrition;
    }

    try {
      const result: any = await dispatch(sendChatMessage(activeSessionId!, {
        role: 'user',
        content: userMessage,
        action_type: actionType,
        metadata
      }));

      if (result.success) {
        dispatch(removeOptimisticMessage(optimisticMessage.message_id));
        if (profile?.id) dispatch(loadUserChatSessions(profile.id));
      } else {
        dispatch(updateMessageStatus(optimisticMessage.message_id, 'failed'));
        setInputMessage(userMessage);
      }
    } catch (error) {
      dispatch(updateMessageStatus(optimisticMessage.message_id, 'failed'));
      setInputMessage(userMessage);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputMessage(suggestion);
  };

  // Helper to send completion message to chat
  const handleActionCompletion = async (messageText: string, metadata: any = {}, actionTypeOverride?: any): Promise<{ success: boolean; error?: string }> => {
    const actionType = actionTypeOverride || selectedActionType || 'new_product';

    // If a session is being created, wait for it and try to get the ID
    let activeSessionId = currentSessionIdRef.current;
    if (!activeSessionId && isCreatingSessionLocal && pendingSessionPromise.current) {
      try {
        const createResult = await pendingSessionPromise.current;
        if (createResult && createResult.success && createResult.data?.session_id) {
          activeSessionId = createResult.data.session_id;
        }
      } catch (error) {
        console.error('Pending session creation failed:', error);
      }
    }

    // Check if currentSessionId is still null but we know a session was just created
    // The Redux state might not have reached us yet in this execution context
    // So we might need to rely on the thunk result if we have it?
    // For now, let's re-check currentSessionId after the await

    if (!activeSessionId) {
      // If still no session exists, create one with this action
      const title = selectedActionType
        ? `${actions.find(a => getActionType(a.id) === actionType)?.title} Session`
        : 'New AI Session';

      try {
        setIsCreatingSessionLocal(true);
        const createPromise = (dispatch as any)(createChatSession({
          first_message: title,
          action_type: actionType
        }));

        pendingSessionPromise.current = createPromise;
        const createResult: any = await createPromise;

        if (createResult.success && createResult.data?.session_id) {
          activeSessionId = createResult.data.session_id;

          const sendResult: any = await (dispatch as any)(sendChatMessage(activeSessionId!, {
            role: 'user',
            content: messageText,
            action_type: actionType,
            metadata
          }));

          if (profile?.id) dispatch(loadUserChatSessions(profile.id));
          return { success: sendResult.success, error: sendResult.error };
        }
        console.error('handleActionCompletion: Failed to create session:', createResult.error);
        return { success: false, error: createResult.error };
      } catch (error) {
        console.error('handleActionCompletion: Error creating session:', error);
        return { success: false, error: error instanceof Error ? error.message : String(error) };
      } finally {
        setIsCreatingSessionLocal(false);
        pendingSessionPromise.current = null;
      }
    } else {
      // Add message to existing session
      const sendResult: any = await (dispatch as any)(sendChatMessage(activeSessionId!, {
        role: 'user',
        content: messageText,
        action_type: actionType,
        metadata
      }));

      if (profile?.id) dispatch(loadUserChatSessions(profile.id));
      return { success: sendResult.success, error: sendResult.error };
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  // Close sidebar when selecting a session on iPad/tablet
  const handleSessionSelectWithClose = (sessionId: string) => {
    handleSessionSelect(sessionId);
    if (isIPad || isTablet) {
      setSidebarOpen(false);
    }
  };

  const handleDeleteRequest = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSessionToDelete(sessionId);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (sessionToDelete) {
      await (dispatch as any)(deleteChatSession(sessionToDelete));
      setShowDeleteConfirm(false);
      setSessionToDelete(null);
    }
  };

  // Close sidebar when creating new chat on iPad/tablet
  const handleNewChatWithClose = () => {
    handleNewChat();
    if (isIPad || isTablet) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className={`ai-chat-dashboard ${className} ${isIPad ? 'ipad-layout' : ''} ${isTablet ? 'tablet-layout' : ''} ${isIPadPro ? 'ipad-pro-layout' : ''}`}>
      {/* Mobile/iPad Header - Always show on iPad */}
      <div className={`mobile-header ${isIPad || isTablet ? 'd-flex' : 'd-md-none'}`}>
        <div className="mobile-header-content">
          <button
            className="btn btn-link sidebar-toggle"
            onClick={toggleSidebar}
            aria-label="Toggle chat history"
            title="Chat History"
          >
            <Menu size={20} />
          </button>
          <h5 className="mobile-header-title">
            AI Generate
          </h5>
          <button
            className="btn btn-link new-chat-mobile"
            onClick={handleNewChatWithClose}
            aria-label="New chat"
            title="New Chat"
          >
            <Plus size={20} />
          </button>
        </div>
      </div>

      <div className="dashboard-layout">
        {/* Session Sidebar */}
        <SessionSidebar
          sessions={userSessions}
          currentSessionId={currentSessionId}
          isLoading={isLoadingUserSessions}
          onSessionSelect={handleSessionSelectWithClose}
          onNewChat={handleNewChatWithClose}
          isOpen={sidebarOpen}
          onToggle={toggleSidebar}
          onSessionDelete={handleDeleteRequest}
        />

        {/* Main Content */}
        <div className="main-content">
          {/* Desktop Header - Hide on iPad/tablet */}
          <div className={`content-header ${isIPad || isTablet ? 'd-none' : 'd-none d-md-block'}`}>
            <div className="header-content">
              <div className="header-left">
                <h1 className="header-title">
                  AI Generate
                </h1>
                <p className="header-subtitle">
                  Create products, formulations, and optimize recipes with JourneyAI
                </p>
              </div>
              <div className="header-right">
                <Button
                  color="primary"
                  onClick={handleNewChat}
                  className="new-chat-btn"
                >
                  <Plus size={16} />
                  New Chat
                </Button>
              </div>
            </div>
          </div>

          {/* Error Messages */}
          {(createSessionError || sendMessageError || loadSessionError) && (
            <Alert color="danger" className="mx-3">
              {createSessionError || sendMessageError || loadSessionError}
            </Alert>
          )}

          <div className="chat-container">
            {/* Messages Area */}
            <div className="messages-area">
              {/* Show loading state when loading session */}
              {isLoadingSession ? (
                <ChatLoading message="Loading conversation..." />
              ) : (
                <>
                  {/* Show welcome message when no messages exist */}
                  {chatMessages.length === 0 && (
                    <div className="welcome-hero-container">
                      <div className="hero-content text-center">
                        <div className="hero-icon-wrapper mb-4">
                          <div className="hero-icon ai-pulse">
                            <MessageCircle size={48} className="text-white" />
                          </div>
                        </div>
                        <h2 className="hero-title mb-2">
                          {currentSessionId ? 'Ready to Assist' : 'Welcome to JourneyAI'}
                        </h2>
                        <p className="hero-subtitle text-muted mb-4 mx-auto" style={{ maxWidth: '500px' }}>
                          {currentSessionId
                            ? (selectedActionType
                              ? `I'm ready to help you ${actions.find(a => getActionType(a.id) === selectedActionType)?.title.toLowerCase()}. Provide your details below to get started.`
                              : 'Select an action type to unlock specialized AI capabilities for your product development.')
                            : 'Experience the future of food product development. Generate concepts, calculate performance scores, and optimize recipes with ease.'
                          }
                        </p>
                        {!currentSessionId && !selectedActionType && (
                          <div className="hero-features d-flex justify-content-center gap-4 mb-4 flex-wrap">
                            <div className="feature-item"><Check size={16} className="text-success me-2" /> AI Product Generation</div>
                            <div className="feature-item"><Check size={16} className="text-success me-2" /> Recipe Optimization</div>
                            <div className="feature-item"><Check size={16} className="text-success me-2" /> Nutrition Scoring</div>
                          </div>
                        )}
                        {!currentSessionId && (
                          <Button color="primary" size="lg" className="px-5 rounded-pill shadow" onClick={() => setShowSessionTitleModal(true)}>
                            Start New Conversation
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Chat Messages */}
                  {chatMessages.map((message) => (
                    <ChatMessage
                      key={message.message_id}
                      message={message}
                    />
                  ))}

                  {/* AI Typing Indicator */}
                  {isAITyping && (
                    <ChatMessage
                      message={{
                        message_id: 'typing',
                        timestamp: new Date().toISOString(),
                        role: 'assistant',
                        content: 'Typing...'
                      }}
                      isTyping={true}
                    />
                  )}

                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Quick Action Cards - Always visible */}
            {!isLoadingSession && (
              <div className={`quick-actions ${chatMessages.length > 0 ? 'compact' : ''}`}>
                <div className="action-cards-row">
                  {actions.map((action) => {
                    const actionType = getActionType(action.id);
                    const isSelected = selectedActionType === actionType;
                    const isDisabled = isCreatingSession || isCreatingSessionLocal;

                    return (
                      <div
                        key={action.id}
                        className={`quick-action-card ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                        onClick={() => !isDisabled && handleActionClick(action.id)}
                        data-testid={`action-card-${action.id}`}
                      >
                        <div className="card-icon">{action.icon}</div>
                        <div className="card-content">
                          <h6 className="card-title">{action.title}</h6>
                          {chatMessages.length === 0 && (
                            <p className="card-description">{action.description}</p>
                          )}
                        </div>
                        {isSelected && (
                          <div className="selection-indicator">
                            <div className="check-icon">✓</div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {selectedActionType && (
                  <div className="action-selection-info">
                    <div className="selection-text">
                      <span className="selection-label">Selected:</span>
                      <span className="selection-value">
                        {actions.find(a => getActionType(a.id) === selectedActionType)?.title}
                      </span>
                    </div>
                    <button
                      className="change-selection-btn"
                      onClick={() => setSelectedActionType(null)}
                      disabled={isCreatingSession || isCreatingSessionLocal}
                    >
                      Change Selection
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Chat Input Area */}
            {!isLoadingSession && (
              <div className="chat-input-area p-3 border-top bg-white">
                <div className="input-group-container">
                  {/* Specialized Inline Forms based on Action Type */}

                  {/* New Product Inline Form */}
                  {selectedActionType === 'new_product' && (
                    <div className="action-form-panel mb-3 p-3 bg-light rounded border">
                      <h6 className="mb-3 d-flex align-items-center"><Plus size={18} className="me-2 text-primary" /> New Product Concept</h6>
                      <FormGroup>
                        <Label for="newProductName" className="small fw-bold">Product Name (Optional)</Label>
                        <Input
                          id="newProductName"
                          placeholder="e.g., Summer Berry Yogurt"
                          value={newProductName}
                          onChange={(e) => setNewProductName(e.target.value)}
                          className="form-control-sm mb-2"
                        />
                      </FormGroup>
                      <FormGroup className="mb-0">
                        <Label for="productDesc" className="small fw-bold">Concept Description *</Label>
                        <textarea
                          id="productDesc"
                          className="form-control chat-input"
                          placeholder="Describe the product you want to create..."
                          value={inputMessage}
                          onChange={(e) => setInputMessage(e.target.value)}
                          rows={2}
                          disabled={isGeneratingProduct}
                        />
                      </FormGroup>
                      <div className="d-flex justify-content-end mt-3">
                        <Button color="primary" onClick={handleChatNewProduct} disabled={!inputMessage.trim() || isGeneratingProduct}>
                          {isGeneratingProduct ? <Spinner size="sm" /> : <><Send size={14} className="me-1" /> Generate Product</>}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Score Calculation Inline Form */}
                  {selectedActionType === 'score' && (
                    <div className="action-form-panel mb-3 p-3 bg-light rounded border">
                      <h6 className="mb-3 d-flex align-items-center"><BarChart2 size={18} className="me-2 text-primary" /> Performance Scores</h6>
                      <FormGroup>
                        <Label for="scoreProductSelect" className="small fw-bold">Select Product *</Label>
                        <Input
                          type="select"
                          id="scoreProductSelect"
                          value={selectedScoreProductId}
                          onChange={(e) => setSelectedScoreProductId(e.target.value)}
                          className="form-control-sm"
                          disabled={isCalculatingScore || isLoadingProducts}
                        >
                          <option value="">{isLoadingProducts ? "Loading products..." : "-- Select a product to analyze --"}</option>
                          {allProducts.map(product => (
                            <option key={product.id} value={product.id}>
                              {product.name} ({product.product_status ? 'Active' : 'Concept'})
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                      <div className="d-flex justify-content-end mt-3">
                        <Button color="primary" onClick={handleChatScoreCalculation} disabled={!selectedScoreProductId || isCalculatingScore}>
                          {isCalculatingScore ? <Spinner size="sm" /> : <><Zap size={14} className="me-1" /> Calculate Scores</>}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Formulate Inline Form */}
                  {selectedActionType === 'formulate' && (
                    <div className="action-form-panel mb-3 p-3 bg-light rounded border">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0 d-flex align-items-center"><FileText size={18} className="me-2 text-primary" /> Formulate Recipe</h6>
                        <div className="btn-group btn-group-sm">
                          <Button color={formulateMode === 'quick' ? 'primary' : 'secondary'} size="sm" onClick={() => setFormulateMode('quick')}>Quick</Button>
                          <Button color={formulateMode === 'detailed' ? 'primary' : 'secondary'} size="sm" onClick={() => setFormulateMode('detailed')}>Detailed</Button>
                        </div>
                      </div>

                      <FormGroup>
                        <Label for="formulateDesc" className="small fw-bold">Requirements Description *</Label>
                        <textarea
                          id="formulateDesc"
                          className="form-control chat-input"
                          placeholder="e.g., High protein energy bar with low sugar..."
                          value={inputMessage}
                          onChange={(e) => setInputMessage(e.target.value)}
                          rows={2}
                          disabled={isGeneratingFormulation}
                        />
                      </FormGroup>

                      {formulateMode === 'detailed' && (
                        <div className="advanced-fields mt-3 pt-3 border-top">
                          <Row>
                            <Col md={6}>
                              <FormGroup>
                                <Label className="small fw-bold">Type</Label>
                                <Input type="text" placeholder="e.g., Beverage" value={formulateType} onChange={(e) => setFormulateType(e.target.value)} className="form-control-sm" />
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup>
                                <Label className="small fw-bold">Serving (g)</Label>
                                <Input type="number" value={formulateServingSize} onChange={(e) => setFormulateServingSize(parseFloat(e.target.value))} className="form-control-sm" />
                              </FormGroup>
                            </Col>
                            <Col md={3}>
                              <FormGroup>
                                <Label className="small fw-bold">Max Cost ($)</Label>
                                <Input type="number" value={formulateMaxCost} onChange={(e) => setFormulateMaxCost(e.target.value)} className="form-control-sm" />
                              </FormGroup>
                            </Col>
                          </Row>

                          <Label className="small fw-bold mt-2">Target Nutrition</Label>
                          <Row className="g-2">
                            {['calories', 'protein', 'carbs', 'fat', 'fiber'].map(nut => (
                              <Col key={nut} xs={4} md={2}>
                                <Label className="x-small text-muted text-capitalize mb-1">{nut}</Label>
                                <Input
                                  bsSize="sm"
                                  placeholder="0.0"
                                  value={formulateNutrition[nut as keyof typeof formulateNutrition]}
                                  onChange={(e) => setFormulateNutrition({ ...formulateNutrition, [nut]: e.target.value })}
                                />
                              </Col>
                            ))}
                          </Row>

                          <Row className="mt-3">
                            <Col md={6}>
                              <Label className="small fw-bold">Allergen Free</Label>
                              <div className="d-flex flex-wrap gap-2">
                                {COMMON_ALLERGENS.slice(0, 4).map(a => (
                                  <CustomInput
                                    key={a} type="checkbox" id={`form-all-${a}`} label={a} className="small"
                                    checked={formulateAllergens.includes(a)}
                                    onChange={(e) => e.target.checked ? setFormulateAllergens([...formulateAllergens, a]) : setFormulateAllergens(formulateAllergens.filter(x => x !== a))}
                                  />
                                ))}
                              </div>
                            </Col>
                            <Col md={6}>
                              <Label className="small fw-bold">Dietary</Label>
                              <div className="d-flex flex-wrap gap-2">
                                {DIETARY_OPTIONS.slice(0, 4).map(d => (
                                  <CustomInput
                                    key={d} type="checkbox" id={`form-diet-${d}`} label={d} className="small"
                                    checked={formulateDietary.includes(d)}
                                    onChange={(e) => e.target.checked ? setFormulateDietary([...formulateDietary, d]) : setFormulateDietary(formulateDietary.filter(x => x !== d))}
                                  />
                                ))}
                              </div>
                            </Col>
                          </Row>
                        </div>
                      )}

                      <div className="d-flex justify-content-end mt-3">
                        <Button color="primary" onClick={handleChatFormulation} disabled={!inputMessage.trim() || isGeneratingFormulation}>
                          {isGeneratingFormulation ? <Spinner size="sm" /> : <><Send size={14} className="me-1" /> Generate Formulation</>}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Optimization Inline Form */}
                  {selectedActionType === 'optimize' && (
                    <div className="action-form-panel mb-3 p-3 bg-light rounded border">
                      <h6 className="mb-3 d-flex align-items-center"><Zap size={18} className="me-2 text-primary" /> Optimize Recipe</h6>
                      <Row>
                        <Col md={7}>
                          <FormGroup>
                            <Label for="optRecipeSelect" className="small fw-bold">Select Recipe *</Label>
                            <Input
                              type="select"
                              id="optRecipeSelect"
                              value={selectedOptimizationRecipeId}
                              onChange={(e) => setSelectedOptimizationRecipeId(e.target.value)}
                              className="form-control-sm"
                              disabled={isOptimizingRecipe || isLoadingFormulations}
                            >
                              <option value="">{isLoadingFormulations ? "Loading recipes..." : "Select a recipe to optimize..."}</option>
                              {allFormulations.map(f => (
                                <option key={f.id} value={f.id}>{f.name || 'Unnamed Recipe'}</option>
                              ))}
                            </Input>
                          </FormGroup>
                        </Col>
                        <Col md={5}>
                          <FormGroup>
                            <Label for="optObjectiveSelect" className="small fw-bold">Objective</Label>
                            <Input
                              type="select"
                              id="optObjectiveSelect"
                              value={selectedOptimizationObjective}
                              onChange={(e) => setSelectedOptimizationObjective(e.target.value)}
                              className="form-control-sm"
                              disabled={isOptimizingRecipe}
                            >
                              <option value="lower_sugar">Lower Sugar</option>
                              <option value="improve_sustainability">Improve Sustainability</option>
                              <option value="reduce_cost">Reduce Cost</option>
                              <option value="boost_protein">Boost Protein</option>
                              <option value="clean_label">Clean Label</option>
                            </Input>
                          </FormGroup>
                        </Col>
                      </Row>

                      <div className="mb-2">
                        <Button color="link" size="sm" onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} className="p-0 text-decoration-none small">
                          {showAdvancedOptions ? "Hide Constraints" : "Show Advanced Constraints..."}
                        </Button>
                      </div>

                      {showAdvancedOptions && (
                        <div className="advanced-fields p-3 bg-white rounded border mb-3">
                          <Row>
                            <Col md={6}>
                              <FormGroup>
                                <Label className="small fw-bold">Max Cost Increase (%)</Label>
                                <Input type="number" value={optimizeMaxCostIncrease} onChange={(e) => setOptimizeMaxCostIncrease(parseFloat(e.target.value))} className="form-control-sm" />
                              </FormGroup>
                            </Col>
                            <Col md={6} className="d-flex align-items-center pt-2">
                              <CustomInput type="switch" id="maintainTaste" label="Maintain Taste Profile" checked={optimizeMaintainTaste} onChange={(e) => setOptimizeMaintainTaste(e.target.checked)} />
                            </Col>
                          </Row>
                          <Row className="mt-2">
                            <Col md={6}>
                              <Label className="small fw-bold">Allergen Restrictions</Label>
                              <div className="d-flex flex-wrap gap-1">
                                {COMMON_ALLERGENS.slice(0, 4).map(a => (
                                  <CustomInput
                                    key={a} type="checkbox" id={`opt-all-${a}`} label={a} className="small"
                                    checked={optimizeAllergens.includes(a)}
                                    onChange={(e) => e.target.checked ? setOptimizeAllergens([...optimizeAllergens, a]) : setOptimizeAllergens(optimizeAllergens.filter(x => x !== a))}
                                  />
                                ))}
                              </div>
                            </Col>
                            <Col md={6}>
                              <Label className="small fw-bold">Dietary Requirements</Label>
                              <div className="d-flex flex-wrap gap-1">
                                {DIETARY_OPTIONS.slice(0, 4).map(d => (
                                  <CustomInput
                                    key={d} type="checkbox" id={`opt-diet-${d}`} label={d} className="small"
                                    checked={optimizeDietary.includes(d)}
                                    onChange={(e) => e.target.checked ? setOptimizeDietary([...optimizeDietary, d]) : setOptimizeDietary(optimizeDietary.filter(x => x !== d))}
                                  />
                                ))}
                              </div>
                            </Col>
                          </Row>
                        </div>
                      )}

                      <div className="d-flex justify-content-end mt-2">
                        <Button color="primary" onClick={handleChatOptimization} disabled={!selectedOptimizationRecipeId || isOptimizingRecipe}>
                          {isOptimizingRecipe ? <Spinner size="sm" /> : <><Zap size={14} className="me-1" /> Run Optimization</>}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Standard Message Input (only shown when no specialized form is active or for general chat) */}
                  {!selectedActionType && (
                    <div className="input-group shadow-sm rounded-pill overflow-hidden border">
                      <textarea
                        className="form-control border-0 px-4 py-3 chat-input"
                        placeholder="Type a message or select an action above..."
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        rows={1}
                        disabled={isSendingMessage || isCreatingSession || isCreatingSessionLocal}
                        style={{ resize: 'none' }}
                      />
                      <div className="input-group-append bg-white d-flex align-items-center pr-3">
                        <button
                          className="btn-icon send-button text-primary"
                          onClick={handleSendMessage}
                          disabled={!inputMessage.trim() || isSendingMessage || isCreatingSession || isCreatingSessionLocal}
                        >
                          {isSendingMessage || isCreatingSession || isCreatingSessionLocal ? (
                            <Spinner size="sm" />
                          ) : (
                            <Send size={20} />
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Legacy Modals - Keep for backward compatibility */}
      <GenerateProductModal
        isOpen={showGenerateModal}
        toggle={() => setShowGenerateModal(false)}
        onProductGenerated={(product) => {
          setShowGenerateModal(false);
          if (product) {
            handleActionCompletion(
              `I have generated a new product: ${product.name || 'New Product'}`,
              { type: 'product_generated', data: product },
              'new_product'
            );
          }
        }}
      />

      <FormulationModal
        isOpen={showFormulationModal}
        toggle={() => {
          setShowFormulationModal(false);
          setModalPrefillData(null);
        }}
        prefillData={modalPrefillData}
        onFormulationCreated={(data) => {
          setShowFormulationModal(false);
          setModalPrefillData(null);
          if (data) {
            handleActionCompletion(
              `New formulation created${data.name ? `: ${data.name}` : ''}`,
              { type: 'formulation_created', data },
              'formulate'
            );
          }
        }}
      />

      <ScoreCalculationModal
        isOpen={showScoreModal}
        toggle={() => {
          setShowScoreModal(false);
          setModalPrefillData(null);
        }}
        onScoreCalculated={(data) => {
          setShowScoreModal(false);
          setModalPrefillData(null);
          if (data) {
            handleActionCompletion(
              `Performance scores calculated${data.productName ? ` for ${data.productName}` : ''}`,
              { type: 'score_calculated', data },
              'score'
            );
          }
        }}
      />

      <OptimizationModal
        isOpen={showOptimizationModal}
        toggle={() => {
          setShowOptimizationModal(false);
          setModalPrefillData(null);
        }}
        onOptimizationComplete={(data) => {
          setShowOptimizationModal(false);
          setModalPrefillData(null);
          if (data) {
            handleActionCompletion(
              `Recipe optimization completed${data.recipeName ? ` for ${data.recipeName}` : ''}`,
              { type: 'optimization_completed', data },
              'optimize'
            );
          }
        }}
      />

      {/* Session Title Modal */}
      {showSessionTitleModal && (
        <div className="session-title-modal-overlay">
          <div className="session-title-modal">
            <div className="modal-header">
              <h4>Create New Session</h4>
              <button
                className="close-btn"
                onClick={handleCancelSessionCreation}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="session-title">Session Title</label>
                <input
                  id="session-title"
                  type="text"
                  className="form-control"
                  placeholder={
                    selectedActionType
                      ? `${actions.find(a => getActionType(a.id) === selectedActionType)?.title} Session`
                      : "Enter session title..."
                  }
                  value={sessionTitle}
                  onChange={(e) => setSessionTitle(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && sessionTitle.trim()) {
                      handleCreateSession();
                    }
                  }}
                  autoFocus
                />
              </div>
              {selectedActionType && (
                <div className="selected-action-info">
                  <span className="action-label">Action Type:</span>
                  <span className="action-value">
                    {actions.find(a => getActionType(a.id) === selectedActionType)?.title}
                  </span>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={handleCancelSessionCreation}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleCreateSession}
                disabled={!sessionTitle.trim() || isCreatingSession}
              >
                {isCreatingSession ? 'Creating...' : 'Create Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteConfirm} toggle={() => setShowDeleteConfirm(!showDeleteConfirm)} centered className="delete-confirmation-modal">
        <ModalHeader toggle={() => setShowDeleteConfirm(!showDeleteConfirm)}>
          Delete Chat Session
        </ModalHeader>
        <ModalBody>
          Are you sure you want to delete this chat session? This action cannot be undone and all messages in this conversation will be permanently removed.
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleConfirmDelete}>
            Delete Session
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default AIChatDashboard;